import {
  Component,
  computed,
  ContentChild,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { InitializedEvent } from "devextreme/ui/select_box";
import { nextRuntimeId } from "src/app/support/runtime-id";
import dxTextBox from "devextreme/ui/text_box";
import { Bind } from "src/app/support/decorators/bind";
import DataSource from "devextreme/data/data_source";
import { isArray, isFunction, isString, some } from "lodash-es";
import { Lambda } from "src/app/support/lambda";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import {
  CodiceFiscaleHelperData,
  CodiceFiscaleResult,
  CodiceFiscaleService
} from "src/app/services/codice-fiscale.service";
import { EditableSelectBoxItemTemplateDirective } from "src/app/components/ui/editable/editable-select-box-item-template.directive";
import { DxFormComponent } from "devextreme-angular";
import {
  ValidationRules,
  ValidationService
} from "src/app/services/validation.service";
import { formatAddress } from "src/app/common/format/address-format";
import { IndirizzoServiceSearchResult } from "src/app/services/indirizzo.service";
import { sntConfirm } from "src/app/common/dialog";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { SsnNotUniqError } from "src/app/errors/ssn-not-uniq.error";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import { Address } from "src/app/backend/models/address";
import { VatNumberNotUniqError } from "src/app/errors/vat-number-not-uniq.error";
import {
  getVatNumberCountriesWithoutItaly,
  validateEUVatNumber,
  validateGlobalVatNumber
} from "src/app/support/vat-number";
import { TooltipDirective } from "src/app/modules/bootstrap/directives/tooltip.directive";
import { WysiwygEditorComponent } from "src/app/components/ui/wysiwyg-editor/wysiwyg-editor.component";
import {
  buildPhoneNumberWithPrefix,
  getDefaultPrefixPhone,
  getPhoneNumber,
  getRegionCodePhoneNumber,
  validatePhone
} from "src/app/support/phone";
import ArrayStore from "devextreme/data/array_store";
import { getPrefixNumber } from "src/app/support/customer";
import { hasText } from "src/app/support/text";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { PLATFORM_TOKEN } from "src/app/tokens/platform.token";
import { PlatformType } from "src/app/common/platform-type";
import { AddressCheckerFunctionService } from "src/app/modules/bootstrap/pms-ui/functions/address/address-checker/address-checker-function.service";
import { ItemClickEvent } from "devextreme/ui/select_box";
import type { ValueChangedEvent } from "devextreme/ui/tag_box";
import { TagLight } from "src/app/backend/models/tag/tag";
import { colorGetLighter, COLORS } from "src/app/support/colors";
import { el } from "src/app/support/dom";
import { compact, trim } from "lodash";

export type EditableDataType =
  | "text"
  | "textarea"
  | "number"
  | "percent"
  | "date"
  | "time"
  | "datetime"
  | "select"
  | "boolean"
  | "currency"
  | "fiscalCode"
  | "vatCode"
  | "address"
  | "lookup"
  | "custom"
  | "phoneNumber"
  | "html"
  | "tag";

export type EditableChangedEvent<T = any, K = any> = {
  lock: Lambda<void>;
  unlock: Lambda<void>;
  updated: Lambda<void>;
  value: K;
  oldValue: K;
  data: T;
  executeAsync: (fnAsync: () => Promise<void>) => Promise<void>;
  recalculatePercentages?: boolean;
};

const VAT_NUMBER_CODE_AND_COUNTRIES = getVatNumberCountriesWithoutItaly();

@Component({
  selector: "snt-editable",
  templateUrl: "editable.component.html",
  styleUrls: ["editable.component.scss"],
  standalone: false
})
export class EditableComponent implements OnInit, OnChanges {
  @ContentChild("placeholderHtmlDataType")
  contentPlaceholderHtml: ElementRef | undefined;

  @ViewChild(DxFormComponent)
  dxForm: DxFormComponent | undefined;

  @Input()
  value: any | undefined;
  valueDisplay: any | undefined;

  @Input()
  data: any | undefined;

  @Input()
  enabled: boolean = true;

  @Input()
  noPaddingLeft: boolean = false;

  @Input()
  noBorder: boolean = false;

  @Input()
  showIcon: boolean = true;

  @Input()
  iconOnTheLeft: boolean = false;

  @Input()
  placeholder?: string;

  @Input()
  fontBold: boolean = false;

  @Input()
  textLight: boolean = false;

  @Input()
  editInline: boolean = false;

  @Input()
  fontSize?: "text-3xl" | "text-xs";

  @Input()
  clickTargetEdit: "icon" | "component" = "icon";

  @Input()
  sup: boolean | undefined;

  @Input()
  dataType: EditableDataType = "text";

  @Input()
  mailto: boolean = false;

  @Input()
  dataSource?: DataSource | Array<any>;

  @Input()
  displayModeDisplayExprData?: () => any;

  @Input()
  displayModeDisplayExprFn?: (data: any) => string;

  @Input()
  mask: string | undefined;

  @Input()
  min: any | undefined;

  @Input()
  max: any | undefined;

  @Input()
  displayExpr?: string | ((value: any) => string);

  @Input()
  tagValueExpr?: string;

  @Input()
  tagDataSourceValueExpr?: string;

  @Input()
  displayOnlyExpr?: string;

  @Input()
  displayPlaceholder?: string;

  @Input()
  fiscalCodeHelperData?: CodiceFiscaleHelperData;

  @Input()
  fiscalCodeCheckConsistencyFn: ((ssn: string) => boolean) | undefined;

  @Input()
  fiscalCodeCheckUniqueness: boolean = false;

  @Input()
  vatNumberCheckUniqueness: boolean = false;

  @Input()
  vatNumberForeign: boolean = false;

  @Input()
  fiscalCodeOptional: boolean = false;

  @Input()
  editingWidth: number | undefined;

  @Input()
  validationGroup: string | undefined;

  @Input()
  infoText: string | undefined;

  @Input()
  showIconWhenValueEmpty: boolean = false;

  editingDropdownOptionsContainerAttachToBody = input<boolean>(false);

  flex = input<boolean>(false);

  tagValue = signal<string[] | null>(null);

  //TODO: gestire anche per non inline
  selectButtons = input<any[] | undefined>(undefined);

  get validationRulesInput(): ValidationRules {
    return this.validationRules();
  }

  @Input({ alias: "validationRules" })
  set validationRulesInput(value: ValidationRules) {
    this.validationRules.set(value);
  }

  validationRules = signal<ValidationRules>([]);

  isRequired = computed<boolean>(() => {
    return some(this.validationRules(), (x) => x.type === "required");
  });

  @Input()
  showClearButton: boolean = false;

  @Input()
  showEditableClearButton: boolean = false;

  @Input()
  label: string | undefined;

  @Input()
  showLabelOnlyOnEdit: boolean = false;

  @Input()
  showLabelOnlyOnDisplay: boolean = false;

  @Input()
  editWidth: number | undefined;

  @Input()
  labelSmall: boolean | undefined;

  @Input()
  icon: string | undefined;

  @Input()
  selectSearchEnabled: boolean = true;

  @Input()
  selectMinSearchLength: number = 3;

  @Input()
  lookupTitle: string | undefined;

  @Input()
  showLabelBottomBorder: boolean = false;

  @Input()
  labelLight: boolean = false;

  @Input()
  width: number | undefined;

  @Input()
  dateDisplayFormat?: string;

  @Input()
  dateTimeOptions?: {
    minute?: {
      readonly?: boolean;
    };
  };

  //TODO: manage acceptCustomValue for dxi-item, default true caused bug SNT-8375
  @Input()
  acceptCustomValue: boolean = true;

  onOpened: (e: any) => void = (e) => {};

  enableDelete = input<boolean>();

  enableRecalculatePercentage = input<boolean>(false);
  recalculatePercentage: boolean = true;

  linkable = input(false);

  cursor = input<"default" | "pointer">("default");

  // default su DESKTOP
  textNoWrap = input<boolean>(inject(PLATFORM_TOKEN) === PlatformType.DESKTOP);

  @Output()
  changed: EventEmitter<EditableChangedEvent> =
    new EventEmitter<EditableChangedEvent>();

  @Output()
  deleted: EventEmitter<EditableChangedEvent> =
    new EventEmitter<EditableChangedEvent>();

  @Output()
  clear: EventEmitter<EditableChangedEvent> =
    new EventEmitter<EditableChangedEvent>();

  @Output()
  codiceFiscaleResult: EventEmitter<CodiceFiscaleResult> =
    new EventEmitter<CodiceFiscaleResult>();

  @Output()
  linkClick: EventEmitter<void> = new EventEmitter();

  @ContentChild(EditableSelectBoxItemTemplateDirective)
  selectBoxItemTemplate: EditableSelectBoxItemTemplateDirective | undefined;

  isEditing: boolean = false;
  isUpdating: boolean = false;
  isClosing: boolean = false;

  positionDialog = input<{
    left?: number;
    top?: number;
    width?: number;
    height?: number;
  }>({
    left: undefined,
    top: undefined,
    width: undefined,
    height: undefined
  });

  inputClientRect:
    | {
        left: number;
        top: number;
        width: number;
        height: number;
      }
    | undefined;

  validationStatus: "valid" | "invalid" | "validating" = "valid";
  validationTouched: boolean = false;

  formData: (any & { value: any }) | undefined;

  lockedOpened: boolean = false;

  dxEditorType:
    | "dxTextBox"
    | "dxTextArea"
    | "dxNumberBox"
    | "dxDateBox"
    | "dxSelectBox"
    | "dxLookup"
    | undefined;

  dxWidth: number | undefined;
  dxDateBoxType: "date" | "time" | "datetime" | undefined;
  dxDateBoxInterval: number | undefined;

  private readonly id = nextRuntimeId();
  private readonly fiscalCodeService = inject(CodiceFiscaleService);
  private readonly customerBakend = inject(CustomerBackend);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly addressCheckerFunctionService = inject(
    AddressCheckerFunctionService
  );

  readonly elementRef: ElementRef = inject(ElementRef);

  validationService = inject(ValidationService);

  errors: string[] = [];

  readonly VAT_NUMBER_CODE_AND_COUNTRIES = VAT_NUMBER_CODE_AND_COUNTRIES;

  dirty: boolean = false;

  prefixPhoneDataSource!: DataSource;

  constructor() {
    effect(() => {
      const validationRules = this.validationRules();
      if (validationRules && validationRules.length > 0) {
        this.validationService.addToGroup(this.validationGroup!, {
          validate: () => {
            this.validationTouched = true;

            const validationErrors = this.validationService.validateRules(
              this.value,
              validationRules
            );

            this.validationStatus =
              validationErrors.length === 0 ? "valid" : "invalid";

            return validationErrors.length === 0;
          }
        });
      }
    });
  }

  async ngOnInit(): Promise<void> {
    switch (this.dataType) {
      case "text":
      case "fiscalCode":
      case "vatCode":
      case "phoneNumber":
      case "address": {
        this.dxEditorType = "dxTextBox";
        break;
      }
      case "textarea": {
        this.dxEditorType = "dxTextArea";
        break;
      }
      case "number":
      case "percent":
      case "currency": {
        this.dxEditorType = "dxNumberBox";
        break;
      }
      case "date":
      case "time":
      case "datetime": {
        this.dxEditorType = "dxDateBox";
        break;
      }
      case "boolean":
      case "select": {
        this.dxEditorType = "dxSelectBox";
        break;
      }
      case "lookup": {
        this.dxEditorType = "dxLookup";
        break;
      }
    }

    switch (this.dataType) {
      case "datetime": {
        this.dxDateBoxType = "datetime";

        const { minute } = this.dateTimeOptions ?? {};

        if (minute) {
          const { readonly } = minute ?? {};

          if (readonly) {
            this.onOpened = (e: any) => {
              const minuteBox =
                e.component["_strategy"]["_timeView"]["_minuteBox"];
              minuteBox.option("readOnly", true);
            };
          }
        }

        break;
      }
      case "date": {
        this.dxDateBoxType = "date";
        break;
      }
      case "time": {
        this.dxDateBoxType = "time";
        this.dxDateBoxInterval = 15;
        this.dxWidth = 120;
        break;
      }
      case "phoneNumber": {
        const updatedValidationRules: ValidationRules = [
          ...(this.validationRules() || []),
          {
            type: "custom",
            validationCallback: (options) => {
              if (!options.value) {
                return true;
              }

              return validatePhone(
                {
                  phone: options.value,
                  countryCode: this.formData?.prefixPhone ?? undefined
                },
                this.bootstrapService
              );
            }
          }
        ];
        this.validationRules.set(updatedValidationRules);
        break;
      }
      case "fiscalCode": {
        const updatedValidationRules: ValidationRules = [
          ...(this.validationRules() || []),
          {
            type: "custom",
            validationCallback: (options) => {
              if (this.fiscalCodeOptional && !options.value) {
                return true;
              }

              return this.fiscalCodeService.isValid(options.value);
            }
          }
        ];

        if (this.fiscalCodeCheckUniqueness) {
          updatedValidationRules.push({
            type: "async",
            validationCallback: (options) =>
              this.validateUniqSSN({ value: options.value }),
            message: sntTranslate(
              marker("Esiste già un cliente con questo codice fiscale")
            )
          });
        }

        this.validationRules.set(updatedValidationRules);

        const reversed = this.fiscalCodeService.reverse(this.value);
        if (reversed && this.fiscalCodeHelperData) {
          this.fiscalCodeHelperData.dataDiNascita =
            this.fiscalCodeHelperData.dataDiNascita ||
            reversed.birthday.toISOString();
          this.fiscalCodeHelperData.codiceCatastale =
            this.fiscalCodeHelperData.codiceCatastale || reversed.birthplace.cc;
        }

        break;
      }
      case "vatCode": {
        const updatedValidationRules: ValidationRules = [
          ...(this.validationRules() || []),
          {
            type: "custom",
            validationCallback: ({ value }) => {
              if (!value) {
                return true;
              }

              if (this.vatNumberForeign) {
                return validateGlobalVatNumber(value).valid;
              }

              return validateEUVatNumber(value);
            }
          }
        ];

        if (this.vatNumberCheckUniqueness) {
          updatedValidationRules.push({
            type: "async",
            validationCallback: (options) =>
              this.validateUniqVatNumber({ value: options.value }),
            message: sntTranslate(marker("Partita IVA già presente"))
          });
        }

        this.validationRules.set(updatedValidationRules);

        break;
      }
      case "boolean": {
        this.value = !!this.value;

        if (!this.dataSource) {
          this.dataSource = [
            {
              value: false,
              display: sntTranslate(marker("No"))
            },
            {
              value: true,
              display: sntTranslate(marker("Si"))
            }
          ];
        }

        this.selectSearchEnabled = false;

        break;
      }
    }

    this.prefixPhoneDataSource = new DataSource({
      store: new ArrayStore({
        data: getPrefixNumber()
      })
    });

    this.formData = { value: this.value };

    if (this.dataType === "phoneNumber") {
      this.formData.value = getPhoneNumber(this.value, this.bootstrapService);
      this.formData.prefixPhone =
        getRegionCodePhoneNumber(
          this.value,
          this.bootstrapService
        )?.toLowerCase() ||
        getDefaultPrefixPhone(this.bootstrapService)?.toLowerCase() ||
        "";
    }

    if (this.editingWidth) {
      this.dxWidth = this.editingWidth;
    }

    await this.valueToValueDisplay();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const valueChange = changes["value"];

    if (valueChange && !valueChange.isFirstChange()) {
      await this.valueToValueDisplay();
    }

    const dataSourceChange = changes["dataSource"];
    if (dataSourceChange) {
      await this.valueToValueDisplay();
    }

    if (valueChange && this.dataType === "tag" && !!this.tagValueExpr) {
      this.tagValue.set(
        valueChange.currentValue.map(
          (item: Record<string, any>) => item[this.tagValueExpr!]
        )
      );
    }

    if (this.formData && valueChange) {
      this.formData.value = valueChange.currentValue;

      if (this.dataType === "phoneNumber") {
        this.formData.value = getPhoneNumber(this.value, this.bootstrapService);
        this.formData.prefixPhone =
          getRegionCodePhoneNumber(
            this.value,
            this.bootstrapService
          )?.toLowerCase() ||
          getDefaultPrefixPhone(this.bootstrapService)?.toLowerCase() ||
          "";
      }
    }
  }

  onClickDisplayContainer(clickEvent: MouseEvent): void {
    clickEvent.stopImmediatePropagation();
    clickEvent.preventDefault();
  }

  onClickEditContainer(clickEvent: MouseEvent): void {
    clickEvent.stopImmediatePropagation();
    clickEvent.preventDefault();
  }

  onFiscalCodeHelperResult(result: CodiceFiscaleResult): void {
    this.dxForm?.instance.updateData({ value: result.codiceFiscale });
  }

  onSelectPriceSuggestion(price: number): void {
    this.formData!.value = price;
    this.value = price;
    this.close();
  }

  async open(
    containerElement: HTMLElement,
    tooltipDirective: TooltipDirective | undefined
  ): Promise<void> {
    tooltipDirective?.hideTooltip();

    this.dirty = false;

    const clientRect = containerElement.getBoundingClientRect();

    const positionDialog = this.positionDialog();

    this.inputClientRect = {
      left: positionDialog.left ?? clientRect.left,
      top: positionDialog.top ?? clientRect.top,
      height: positionDialog.height ?? clientRect.height,
      width: positionDialog.width ?? clientRect.width
    };

    this.isEditing = true;
  }

  async delete(): Promise<void> {
    this.deleted.emit({
      lock: () => {
        this.isUpdating = true;
      },
      unlock: () => {
        this.isUpdating = false;
      },
      updated: () => {
        this.isClosing = false;
        this.isEditing = false;
      },
      executeAsync: async (fnAsync: Lambda<Promise<void>>) => {
        try {
          this.isUpdating = true;

          await fnAsync();

          this.isClosing = false;
          this.isEditing = false;
        } catch (ex) {
          //this.formData!.value = this.value;
        } finally {
          this.isClosing = false;
          this.isUpdating = false;
          this.recalculatePercentage = true;
        }
      },
      data: this.data,
      value: this.value,
      oldValue: this.value,
      recalculatePercentages: this.recalculatePercentage
    });
  }

  onClear(): void {
    this.clear.emit({
      lock: () => {
        this.isUpdating = true;
      },
      unlock: () => {
        this.isUpdating = false;
      },
      updated: () => {
        this.isClosing = false;
        this.isEditing = false;
      },
      executeAsync: async (fnAsync: Lambda<Promise<void>>) => {
        try {
          this.isUpdating = true;

          await fnAsync();

          this.isClosing = false;
          this.isEditing = false;
        } catch (ex) {
          this.formData!.value = this.value;

          this.isEditing = false;
        } finally {
          this.isClosing = false;
          this.isUpdating = false;
        }
      },
      data: this.data,
      value: null,
      oldValue: null
    });
  }

  async canRelease(): Promise<boolean> {
    if (this.dxForm && !this.dirty) {
      return true;
    }

    if (this.dxForm) {
      let validation = this.dxForm.instance.validate();

      if (validation.complete) {
        validation = await validation.complete;
      }

      if (!validation.isValid) {
        return false;
      }

      return true;
    } else {
      return true;
    }
  }

  async cancel(): Promise<void> {
    this.validationTouched = false;
    this.validationStatus = "valid";

    if (this.formData) {
      this.formData.value = this.value;
    }

    this.isEditing = false;
  }

  @Bind
  async onSave(
    buttonClickAsyncEvent?: ButtonClickAsyncEvent | ItemClickEvent
  ): Promise<void> {
    if (buttonClickAsyncEvent && !!(<any>buttonClickAsyncEvent).execute) {
      await (<ButtonClickAsyncEvent>buttonClickAsyncEvent).execute(async () => {
        await this.close();
      });
    } else {
      await this.close();
    }
  }

  @Bind
  onInlineEditorInitialized(dxEvent: InitializedEvent): void {
    // FIXME il setTimeout è necessario per ritardare l'invocazione del metodo
    // focus() dopo l'inizializzazione del componente
    setTimeout(() => {
      dxEvent.component?.focus();
    }, 0);
  }

  @Bind
  async onFocusOutSaveOrCancel(): Promise<void> {
    switch (true) {
      case this.dirty: {
        await this.onSave();
        break;
      }
      default: {
        await this.cancel();
        break;
      }
    }
  }

  @Bind
  async onCancel(
    buttonClickAsyncEvent?: ButtonClickAsyncEvent | any
  ): Promise<void> {
    if (buttonClickAsyncEvent && !!(<any>buttonClickAsyncEvent).execute) {
      await buttonClickAsyncEvent.execute(async () => {
        this.isEditing = false;
        this.formData.value = this.value;
      });
    } else {
      this.isEditing = false;
      this.formData.value = this.value;
    }
  }

  onFieldDataChanged(): void {
    this.dirty = true;
  }

  async close(): Promise<void> {
    if (!(await this.canRelease())) {
      return;
    }

    this.validationTouched = false;
    this.validationStatus = "valid";

    let value = this.formData?.value;
    const oldValue = this.value;

    if (value === oldValue) {
      this.isEditing = false;
      return;
    }

    if (this.dataType === "fiscalCode" && this.fiscalCodeCheckConsistencyFn) {
      const isConsistency = this.fiscalCodeCheckConsistencyFn(
        this.formData?.value
      );

      if (!isConsistency) {
        const confirm = await sntConfirm(
          sntTranslate(marker("Codice fiscale non coerente")),
          sntTranslate(
            marker(
              "Il codice fiscale è valido ma non coerente con i dati anagrafici inseriti, vuoi proseguire?"
            )
          )
        );

        if (!confirm) {
          return;
        }
      }
    }

    if (this.dataType === "phoneNumber") {
      value = hasText(value)
        ? buildPhoneNumberWithPrefix(
            { phone: value, countryCode: this.formData?.prefixPhone },
            this.bootstrapService
          )
        : "";
    }

    if (this.dataType === "address") {
      const confirmedAddress = await this.addressCheckerFunctionService.execute(
        {
          originalAddress: this.value,
          updatedAddress: this.formData.value
        }
      );

      if (confirmedAddress) {
        value = confirmedAddress;
        this.formData.value = confirmedAddress;
      } else {
        this.isEditing = false;
        this.formData.value = oldValue;
        return;
      }
    }

    this.isClosing = true;

    this.changed.emit({
      lock: () => {
        this.isUpdating = true;
      },
      unlock: () => {
        this.isUpdating = false;
      },
      updated: () => {
        this.isClosing = false;
        this.isEditing = false;
      },
      executeAsync: async (fnAsync: Lambda<Promise<void>>) => {
        try {
          this.isUpdating = true;

          await fnAsync();

          this.isClosing = false;
          this.isEditing = false;
        } catch (ex) {
        } finally {
          this.isClosing = false;
          this.isUpdating = false;
          this.recalculatePercentage = true;
        }
      },
      data: this.data,
      value: value,
      oldValue: this.value,
      recalculatePercentages: this.recalculatePercentage
    });
  }

  private async valueToValueDisplay(): Promise<void> {
    switch (this.dataType) {
      case "address": {
        this.valueDisplay = formatAddress(this.formData.value);
        break;
      }
      case "lookup":
      case "select": {
        switch (true) {
          case !this.isEditing &&
            !!this.displayModeDisplayExprFn &&
            !!this.displayModeDisplayExprData: {
            this.valueDisplay = this.displayModeDisplayExprFn(
              this.displayModeDisplayExprData
            );
            break;
          }
          case isArray(this.dataSource): {
            this.valueDisplay = getValueDisplayFromArrayDataSource(
              <Array<{ value: string; display: string }>>this.dataSource,
              this.value
            );
            break;
          }
          default: {
            this.valueDisplay = await getValueDisplayFromDxDataSource(
              <DataSource>this.dataSource,
              this.value,
              this.displayOnlyExpr || this.displayExpr
            );
          }
        }
        break;
      }
      case "phoneNumber": {
        this.valueDisplay = getPhoneNumber(this.value, this.bootstrapService);
        break;
      }
      case "tag": {
        this.valueDisplay = undefined;
        break;
      }
      default: {
        if (this.displayExpr) {
          if (isString(this.displayExpr)) {
            this.valueDisplay = this.value[this.displayExpr];
          }

          if (isFunction(this.displayExpr)) {
            this.valueDisplay = this.displayExpr(this.value);
          }
        } else {
          this.valueDisplay = this.value;
        }
        break;
      }
    }
  }

  @Bind
  onEditorContentReady({
    component,
    element
  }: {
    component: dxTextBox;
    element: HTMLElement;
  }): void {
    switch (this.dxEditorType) {
      case "dxTextBox":
      case "dxNumberBox": {
        component.focus();
        (<HTMLInputElement>(
          element.querySelector("input[editableInput=true]")
        ))?.select();
        break;
      }
    }
  }

  @Bind
  selectBoxItemTemplateFn(
    data: any,
    index: number,
    element: HTMLElement
  ): void {
    const embeddedView =
      this.selectBoxItemTemplate?.viewContainer.createEmbeddedView(
        this.selectBoxItemTemplate?.templateRef,
        {
          data
        }
      );

    embeddedView?.rootNodes.forEach((node) => element.appendChild(node));
  }

  onWysiwygEditorContentChanged(
    wysiwygEditorComponent: WysiwygEditorComponent
  ): void {
    this.formData.value = wysiwygEditorComponent.getContent(true);
  }

  addressAdressItemTeplate(
    data: IndirizzoServiceSearchResult,
    index: number,
    element: HTMLElement
  ): void {
    const span = document.createElement("span");
    span.classList.add("p-2");
    span.innerText = data.formattedAddress;

    element.appendChild(span);
  }

  @Bind
  onAddressAddressSelectionChanged(address: Address): void {
    this.formData.value = address;
  }

  @Bind
  onTagValueChanged(dxEvent: ValueChangedEvent): void {
    if (!this.formData) {
      return;
    }

    this.formData.value = dxEvent.value ?? [];
    this.dirty = true;
  }

  @Bind
  tagTemplate(tag: TagLight | any, tagElement: HTMLElement) {
    const DEFAULT_COLOR = COLORS.CARCOAL_GRAY;
    const borderColor = tag.color || DEFAULT_COLOR;
    const textColor = colorGetLighter(tag.color || DEFAULT_COLOR, 85);
    const backgroundColor = tag.color || DEFAULT_COLOR;
    const displayValue: string =
      (Object.entries(tag ?? {})
        .filter(([key, value]) => key === this.displayExpr)
        .map(([key, value]) => value)
        .at(0) as string) ?? "";

    return el(
      "div",
      {
        class: trim(`dx-tag-content text-xs`),
        style: {
          "background-color": backgroundColor,
          "border-width": "1px",
          "border-color": borderColor,
          "padding-left": "2px",
          "padding-right": "22px",
          "padding-top": "0",
          "padding-bottom": "0",
          "margin-top": "7px"
        }
      },
      compact([
        el(
          "span",
          {
            class: "text-xs font-bold",
            style: `color: ${textColor} !important;`
          },
          displayValue
        ),
        el(
          "div",
          {
            class: "dx-tag-remove-button pms-tag-box"
          },
          [
            el("i", {
              class: "fa-solid fa-xmark",
              style: {
                color: textColor,
                "margin-top": "1px",
                "margin-right": "3px"
              }
            })
          ]
        )
      ])
    );
  }

  @Bind
  async validateUniqSSN({ value }: { value: string }): Promise<void> {
    if (!value) {
      return;
    }

    const custmersWithSameSSN = await this.customerBakend
      .searchBySSN(value)
      .toPromise();

    if (custmersWithSameSSN.length > 0) {
      throw new SsnNotUniqError("");
    }
  }

  @Bind
  async validateUniqVatNumber({ value }: { value: string }): Promise<void> {
    if (!value) {
      return;
    }

    const custmersWithSameVatNumber = await this.customerBakend
      .searchByVatNumber(value)
      .toPromise();

    if (custmersWithSameVatNumber.length > 0) {
      throw new VatNumberNotUniqError("");
    }
  }

  getIconFromNumber(value: string): string {
    return (
      getRegionCodePhoneNumber(value, this.bootstrapService) ?? ""
    ).toLowerCase();
  }

  cssClassPhoneNumber(value: string): string {
    return `fi fi-${this.getIconFromNumber(value)}`;
  }

  protected readonly console = console;
}

function getValueDisplayFromArrayDataSource(
  dataSource: Array<{ value: string; display: string }>,
  value: any
): any {
  return dataSource.find((x) => x.value === value)?.display;
}

async function getValueDisplayFromDxDataSource(
  dataSource: DataSource,
  value: any,
  displayExpr?: string | ((value: any) => string)
): Promise<any> {
  const dataSourceValue = await dataSource.store().byKey(value);

  if (!dataSourceValue) {
    return "";
  }

  if (!!displayExpr) {
    if (isString(displayExpr)) {
      return dataSourceValue ? dataSourceValue[displayExpr] : undefined;
    }

    if (isFunction(displayExpr)) {
      return displayExpr(dataSourceValue);
    }
  }

  return dataSourceValue;
}
